import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Plus,
  Save,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Eye,
  Copy,
  Layout,
  Image,
  Images,
  Type,
  Columns,
  MousePointer,
  Table,
  Table2,
  ShoppingBag,
  Paperclip,
  Minus,
  Square,
  Share2,
  Loader2,
  Check,
  X,
  Edit3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { API_BASE, templatesApi, messageTypesApi } from '../lib/api';
import axios from 'axios';

// Icons for section types
const SECTION_ICONS = {
  header: Layout,
  hero: Image,
  text: Type,
  image: Image,
  twoColumn: Columns,
  productList: ShoppingBag,
  pricingTable: Table,
  cta: MousePointer,
  attachment: Paperclip,
  divider: Minus,
  spacer: Square,
  social: Share2,
  footer: Layout,
  imageTable: Table2,
  logoCarousel: Images,
};

// API calls
const templateEditorApi = {
  getSectionTypes: () => axios.get(`${API_BASE}/templates/section-types`),
  createSection: (data) => axios.post(`${API_BASE}/templates/section/create`, data),
  createTemplate: (data) => axios.post(`${API_BASE}/templates/create`, data),
  updateTemplate: (id, data) => axios.put(`${API_BASE}/templates/${id}`, data),
  duplicateTemplate: (id, data) => axios.post(`${API_BASE}/templates/${id}/duplicate`, data),
  deleteTemplate: (id) => axios.delete(`${API_BASE}/templates/${id}`),
};

// Section Type Card in Add Dialog
const SectionTypeCard = ({ type, config, onSelect }) => {
  const Icon = SECTION_ICONS[type] || Layout;
  
  return (
    <button
      onClick={() => onSelect(type)}
      className="flex items-start gap-3 p-4 border rounded-lg text-left hover:border-indigo-300 hover:bg-indigo-50 transition-all"
      data-testid={`section-type-${type}`}
    >
      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-indigo-600" />
      </div>
      <div>
        <p className="font-medium text-slate-900">{config.label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{config.description}</p>
      </div>
    </button>
  );
};

// Section Editor Card
const SectionCard = ({ section, index, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const Icon = SECTION_ICONS[section.type] || Layout;
  
  return (
    <div 
      className="flex items-center gap-3 p-3 bg-white border rounded-lg group hover:shadow-sm transition-all"
      data-testid={`section-item-${section.id}`}
    >
      {/* Move controls - always visible */}
      <div className="flex flex-col gap-0.5">
        <button 
          onClick={onMoveUp} 
          disabled={isFirst}
          className="p-1.5 rounded hover:bg-indigo-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          title="Mover arriba"
        >
          <ChevronDown className="w-4 h-4 rotate-180 text-indigo-600" />
        </button>
        <button 
          onClick={onMoveDown} 
          disabled={isLast}
          className="p-1.5 rounded hover:bg-indigo-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          title="Mover abajo"
        >
          <ChevronDown className="w-4 h-4 text-indigo-600" />
        </button>
      </div>
      
      {/* Position indicator */}
      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-indigo-600">{index + 1}</span>
      </div>
      
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-slate-600" />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{section.label}</p>
        <p className="text-xs text-slate-500">{section.type}</p>
      </div>
      
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-section-${section.id}`}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onDelete}
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          data-testid={`delete-section-${section.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Array Editor Component (for rows, products, columns, etc.)
const ArrayEditor = ({ field, value, onChange, columnsDef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editedArray, setEditedArray] = useState([]);
  
  useEffect(() => {
    const currentValue = Array.isArray(value) ? value : (field.default || []);
    setEditedArray(JSON.parse(JSON.stringify(currentValue)));
  }, [value, field.default, isOpen]);
  
  const handleSave = () => {
    onChange(editedArray);
    setIsOpen(false);
    toast.success('Elementos actualizados');
  };
  
  // Determine columns to display
  // If columnsDef is provided (for rows), use the keys from columns
  // Otherwise, infer from the first item
  const getDisplayColumns = () => {
    if (columnsDef && Array.isArray(columnsDef) && columnsDef.length > 0) {
      // Use column definitions - each column has {header, key}
      return columnsDef.map(col => ({
        key: col.key || col.header?.toLowerCase().replace(/\s+/g, '_') || '',
        header: col.header || col.key || ''
      }));
    }
    // Fallback: infer from first item
    if (editedArray.length > 0) {
      return Object.keys(editedArray[0]).map(key => ({
        key,
        header: key.replace(/([A-Z])/g, ' $1').trim()
      }));
    }
    return [];
  };
  
  const displayColumns = getDisplayColumns();
  
  const addRow = () => {
    const newRow = {};
    displayColumns.forEach(col => {
      newRow[col.key] = '';
    });
    // If no columns defined, use template from first item
    if (displayColumns.length === 0 && editedArray.length > 0) {
      Object.keys(editedArray[0]).forEach(key => {
        newRow[key] = '';
      });
    }
    setEditedArray([...editedArray, newRow]);
  };
  
  const removeRow = (index) => {
    setEditedArray(editedArray.filter((_, i) => i !== index));
  };
  
  const updateRow = (index, key, newValue) => {
    const updated = [...editedArray];
    updated[index] = { ...updated[index], [key]: newValue };
    setEditedArray(updated);
  };
  
  return (
    <div className="space-y-2">
      <Label>{field.label}</Label>
      <div className="flex items-center gap-2">
        <Badge variant="secondary">
          {Array.isArray(value) ? value.length : (field.default?.length || 0)} elementos
        </Badge>
        <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
          <Edit3 className="w-3 h-3 mr-1" />
          Editar
        </Button>
      </div>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar {field.label}</DialogTitle>
            <DialogDescription>
              Añade, edita o elimina elementos de la lista
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editedArray.length === 0 && displayColumns.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay elementos. Añade uno nuevo.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">#</th>
                      {displayColumns.map(col => (
                        <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-slate-500 capitalize whitespace-nowrap">
                          {col.header}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {editedArray.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-sm text-slate-500">{idx + 1}</td>
                        {displayColumns.map(col => (
                          <td key={col.key} className="px-3 py-2">
                            <Input
                              value={row[col.key] || ''}
                              onChange={(e) => updateRow(idx, col.key, e.target.value)}
                              className="h-8 text-sm min-w-[100px]"
                              placeholder={col.header}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(idx)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <Button variant="outline" onClick={addRow} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Añadir fila
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Field Editor
const FieldEditor = ({ field, value, onChange, columnsDef }) => {
  if (field.type === 'textarea') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Textarea
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.default}
          className="min-h-[80px]"
        />
      </div>
    );
  }
  
  if (field.type === 'select' && field.options) {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Select value={value ?? field.default} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  if (field.type === 'number') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Input
          type="number"
          value={value ?? field.default ?? 0}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        />
      </div>
    );
  }
  
  if (field.type === 'array') {
    return <ArrayEditor field={field} value={value} onChange={onChange} columnsDef={columnsDef} />;
  }
  
  return (
    <div className="space-y-2">
      <Label>{field.label}</Label>
      <Input
        type={field.type === 'image' ? 'url' : 'text'}
        value={value ?? field.default ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.type === 'image' ? 'https://...' : field.default}
      />
      {field.type === 'image' && value && (
        <img src={value} alt="Preview" className="mt-2 max-h-20 rounded object-cover" />
      )}
    </div>
  );
};

// Section Detail Editor Dialog
const SectionDetailEditor = ({ section, open, onClose, onSave }) => {
  const [editedSection, setEditedSection] = useState(null);
  
  useEffect(() => {
    if (section) {
      setEditedSection(JSON.parse(JSON.stringify(section)));
    }
  }, [section]);
  
  if (!editedSection) return null;
  
  const handleFieldDefaultChange = (fieldKey, value) => {
    setEditedSection(prev => ({
      ...prev,
      fields: prev.fields.map(f => 
        f.key === fieldKey ? { ...f, default: value } : f
      )
    }));
  };
  
  const handleSave = () => {
    onSave(editedSection);
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Editar sección: {editedSection.label}</DialogTitle>
          <DialogDescription>Configura los campos y valores por defecto</DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre de la sección</Label>
              <Input
                value={editedSection.label}
                onChange={(e) => setEditedSection({ ...editedSection, label: e.target.value })}
                data-testid="section-label-input"
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-900 mb-3">Valores por defecto</h4>
              <div className="space-y-4">
                {editedSection.fields?.map((field) => {
                  // For "rows" field, pass the columns definition
                  let columnsDef = null;
                  if (field.key === 'rows') {
                    const columnsField = editedSection.fields?.find(f => f.key === 'columns');
                    columnsDef = columnsField?.default || [];
                  }
                  
                  return (
                    <FieldEditor
                      key={field.key}
                      field={field}
                      value={field.default}
                      onChange={(value) => handleFieldDefaultChange(field.key, value)}
                      columnsDef={columnsDef}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} data-testid="save-section-btn">
            <Check className="w-4 h-4 mr-2" />
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Template Editor Page
export const TemplateEditor = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditing = !!templateId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionTypes, setSectionTypes] = useState({});
  const [messageTypes, setMessageTypes] = useState([]);
  
  // Template data
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    category: 'newsletter',
    messageTypes: [],
    sectors: ['all'],
    sections: [],
    thumbnail: ''
  });
  
  // Available sectors
  const [availableSectors, setAvailableSectors] = useState([]);
  
  // UI state
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [deletingSection, setDeletingSection] = useState(null);
  
  // Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, msgTypesRes, sectorsRes] = await Promise.all([
          templateEditorApi.getSectionTypes(),
          messageTypesApi.list(),
          axios.get(`${API_BASE}/sectors`),
        ]);
        setSectionTypes(typesRes.data.sectionTypes);
        setMessageTypes(msgTypesRes.data.messageTypes);
        setAvailableSectors(sectorsRes.data.sectors || []);
        
        // Load existing template if editing
        if (templateId) {
          const templateRes = await templatesApi.get(templateId);
          setTemplate(templateRes.data);
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [templateId]);
  
  // Add section
  const handleAddSection = async (sectionType) => {
    try {
      const response = await templateEditorApi.createSection({ sectionType });
      setTemplate(prev => ({
        ...prev,
        sections: [...prev.sections, response.data.section]
      }));
      setShowAddSection(false);
      toast.success('Sección añadida');
    } catch (error) {
      toast.error('Error al crear sección');
    }
  };
  
  // Update section
  const handleUpdateSection = (updatedSection) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === updatedSection.id ? updatedSection : s
      )
    }));
  };
  
  // Delete section
  const handleDeleteSection = () => {
    if (!deletingSection) return;
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== deletingSection.id)
    }));
    setDeletingSection(null);
    toast.success('Sección eliminada');
  };
  
  // Move section
  const moveSection = (index, direction) => {
    const newSections = [...template.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setTemplate(prev => ({ ...prev, sections: newSections }));
  };
  
  // Save template
  const handleSave = async () => {
    if (!template.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (template.sections.length === 0) {
      toast.error('Añade al menos una sección');
      return;
    }
    
    setSaving(true);
    try {
      if (isEditing) {
        await templateEditorApi.updateTemplate(templateId, template);
        toast.success('Plantilla actualizada');
      } else {
        const response = await templateEditorApi.createTemplate(template);
        toast.success('Plantilla creada');
        navigate(`/templates/edit/${response.data.template.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle message type
  const toggleMessageType = (typeId) => {
    setTemplate(prev => ({
      ...prev,
      messageTypes: prev.messageTypes.includes(typeId)
        ? prev.messageTypes.filter(t => t !== typeId)
        : [...prev.messageTypes, typeId]
    }));
  };
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="p-8 animate-fade-in" data-testid="template-editor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/templates')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a plantillas
          </Button>
          <h1 className="text-2xl font-bold font-[Manrope] text-slate-900">
            {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}
          </h1>
        </div>
        
        <Button 
          onClick={handleSave} 
          disabled={saving}
          data-testid="save-template-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Guardando...' : 'Guardar plantilla'}
        </Button>
      </div>
      
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Template Info & Sections */}
        <div className="col-span-5 space-y-6">
          {/* Template Info */}
          <Card>
            <CardHeader>
              <CardTitle>Información de la plantilla</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  placeholder="Ej: Newsletter Mensual"
                  className="mt-2"
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={template.description}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  placeholder="Describe para qué sirve esta plantilla..."
                  className="mt-2"
                  data-testid="template-description-input"
                />
              </div>
              
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={template.category} 
                  onValueChange={(v) => setTemplate({ ...template, category: v })}
                >
                  <SelectTrigger className="mt-2" data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {messageTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Sectores</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={() => {
                      const newSectors = template.sectors?.includes('all') 
                        ? [] 
                        : ['all'];
                      setTemplate({ ...template, sectors: newSectors });
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition-all ${
                      template.sectors?.includes('all')
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {template.sectors?.includes('all') && (
                      <Check className="w-3 h-3 inline mr-1" />
                    )}
                    Todos los sectores
                  </button>
                  {availableSectors.filter(s => s.id !== 'all').map((sector) => (
                    <button
                      key={sector.id}
                      onClick={() => {
                        const currentSectors = template.sectors || [];
                        const withoutAll = currentSectors.filter(s => s !== 'all');
                        const newSectors = withoutAll.includes(sector.id)
                          ? withoutAll.filter(s => s !== sector.id)
                          : [...withoutAll, sector.id];
                        setTemplate({ ...template, sectors: newSectors.length ? newSectors : ['all'] });
                      }}
                      disabled={template.sectors?.includes('all')}
                      className={`px-3 py-1 rounded-full text-sm border transition-all ${
                        template.sectors?.includes(sector.id) && !template.sectors?.includes('all')
                          ? 'bg-green-100 border-green-300 text-green-700'
                          : template.sectors?.includes('all')
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {template.sectors?.includes(sector.id) && !template.sectors?.includes('all') && (
                        <Check className="w-3 h-3 inline mr-1" />
                      )}
                      {sector.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Selecciona los sectores que podrán usar esta plantilla
                </p>
              </div>
              
              <div>
                <Label>Tipos de mensaje compatibles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {messageTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => toggleMessageType(type.id)}
                      className={`px-3 py-1 rounded-full text-sm border transition-all ${
                        template.messageTypes.includes(type.id)
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {template.messageTypes.includes(type.id) && (
                        <Check className="w-3 h-3 inline mr-1" />
                      )}
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Sections */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Secciones</CardTitle>
                <CardDescription>
                  {template.sections.length} secciones • Usa las flechas ↑↓ para reordenar
                </CardDescription>
              </div>
              <Button 
                size="sm" 
                onClick={() => setShowAddSection(true)}
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir
              </Button>
            </CardHeader>
            <CardContent>
              {template.sections.length > 0 ? (
                <div className="space-y-2">
                  {template.sections.map((section, index) => (
                    <SectionCard
                      key={section.id}
                      section={section}
                      index={index}
                      onEdit={() => setEditingSection(section)}
                      onDelete={() => setDeletingSection(section)}
                      onMoveUp={() => moveSection(index, 'up')}
                      onMoveDown={() => moveSection(index, 'down')}
                      isFirst={index === 0}
                      isLast={index === template.sections.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Layout className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay secciones</p>
                  <p className="text-sm">Añade secciones para construir tu plantilla</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Right: Preview */}
        <div className="col-span-7">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Vista previa de estructura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-100 rounded-lg p-4 min-h-[500px]">
                <div className="max-w-[300px] mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
                  {template.sections.length > 0 ? (
                    template.sections.map((section) => {
                      const Icon = SECTION_ICONS[section.type] || Layout;
                      return (
                        <div 
                          key={section.id}
                          className="border-b last:border-b-0 p-3 flex items-center gap-2 hover:bg-slate-50"
                        >
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">{section.label}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-slate-400 text-sm">
                      Añade secciones para ver la estructura
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Add Section Dialog */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Añadir sección</DialogTitle>
            <DialogDescription>Selecciona el tipo de sección que quieres añadir</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(sectionTypes).map(([type, config]) => (
                <SectionTypeCard
                  key={type}
                  type={type}
                  config={config}
                  onSelect={handleAddSection}
                />
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Edit Section Dialog */}
      <SectionDetailEditor
        section={editingSection}
        open={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSave={handleUpdateSection}
      />
      
      {/* Delete Section Confirmation */}
      <AlertDialog open={!!deletingSection} onOpenChange={() => setDeletingSection(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sección?</AlertDialogTitle>
            <AlertDialogDescription>
              La sección "{deletingSection?.label}" será eliminada de la plantilla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSection} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplateEditor;
